import { taxonomies } from '@/kontent/project/taxonomies'
import { NesaSearchResult } from '@/types/searchResult'
import { commonFetch } from '@/utils/fetchUtils'
import { useQuery } from '@tanstack/react-query'

type AzureSearchResponse<TValue, TFacet> = {
	'@odata.context': string
	'@odata.count': number
	'@search.facets': TFacet
	value: ({
		'@search.score': number
	} & TValue)[]
}

type AzureSearchParams = {
	search: string
	count?: boolean
	queryType?: 'simple' | 'full'
	searchMode?: 'all' | 'any'
	skip?: number
	top?: number
	facets?: string[]
	filter?: string
	orderby?: string
	speller?: string
	queryLanguage?: string
	searchFields?: string
}

type AzureSuggestionParams = {
	search: string
	suggesterName: string
	select?: string
	filter?: string
	top?: number
	fuzzy?: boolean
	highlightPostTag?: string
	highlightPreTag?: string
}

type AzureFacets = {
	syllabuses: { count: number; value: string }[]
	stages: { count: number; value: string }[]
	key_learning_area: { count: number; value: string }[]
}

const AZURE_SEARCH_CONFIG = {
	domain: process.env.NEXT_PUBLIC_AZURE_SEARCH_DOMAIN,
	apiKey: process.env.NEXT_PUBLIC_AZURE_SEARCH_API_KEY || '',
	defaultIndex: process.env.NEXT_PUBLIC_AZURE_SEARCH_INDEX,
}

const getAzureHeaders = () => ({
	'Content-Type': 'application/json',
	'api-key': AZURE_SEARCH_CONFIG.apiKey,
})

const getAzureEndpoint = (index: string, endpoint: 'search' | 'suggest') => {
	const apiVersion =
		endpoint === 'search' ? '2024-05-01-preview' : '2016-09-01'
	return `https://${AZURE_SEARCH_CONFIG.domain}/indexes/${index}/docs/${endpoint}?api-version=${apiVersion}`
}

const DEFAULT_QUERY_CONFIG = {
	keepPreviousData: true,
	staleTime: 0,
	refetchOnWindowFocus: false,
}

export const useSyllabusAzureSearch = (
	{
		q,
		page,
		pageSize = 10,
		filter = '',
		orderby = '',
		contentType = '',
		searchFields = '',
	},
	index = AZURE_SEARCH_CONFIG.defaultIndex,
) => {
	const skip = (page - 1) * pageSize

	return useQuery({
		queryKey: [index, q, skip, pageSize, filter, orderby, contentType],
		queryFn: async () => {
			if (!q) {
				return {
					ok: true,
					status: 200,
					json: {
						'@odata.context': '',
						count: 0,
						facets: null,
						value: [],
					},
				}
			}

			const response = await commonFetch<
				AzureSearchResponse<NesaSearchResult, AzureFacets>,
				AzureSearchParams
			>(
				getAzureEndpoint(index, 'search'),
				{
					search: q,
					count: true,
					queryType: 'simple',
					searchMode: 'all',
					speller: 'lexicon',
					queryLanguage: 'en-us',
					skip,
					top: pageSize,
					facets: [
						'syllabuse_codes,count:1000',
						'syllabuse_names,count:1000',
						'stage_codes,count:1000',
						'kla_names,count:1000',
						'resource_type,count:1000',
					],
					filter,
					orderby,
					searchFields,
				},
				{ headers: getAzureHeaders() },
			)

			return {
				...response,
				json: {
					...response.json,
					count: response.json['@odata.count'],
					facets: response.json['@search.facets'],
				},
			}
		},
		...DEFAULT_QUERY_CONFIG,
	})
}

export const useAllSyllabusFromAzureSearch = (
	{ filter = '' } = {},
	index = AZURE_SEARCH_CONFIG.defaultIndex,
) => {
	const { data } = useSyllabusAzureSearch({
		q: '*',
		page: 1,
		pageSize: 1,
		filter,
		orderby: '',
	})

	return (
		data?.json?.facets.syllabuse_codes.map((syllabus, index) => {
			return {
				value: syllabus.value,
				// match the label from taxo syllabus
				label: taxonomies.syllabus.terms[syllabus.value]?.name,
				count: data?.json?.facets.syllabuse_codes?.[index]?.count,
			}
		}) || []
	)
}

const SUGGESTION_CONTENT_TYPES: NesaSearchResult['content_type'][] = [
	'syllabus-overview',
	'syllabus-focus-area',
	'syllabus-teaching-advice',
	'syllabus-outcome',
	'glossary',
	'ace-subgroup',
]

export const useSyllabusAzureSuggestion = (
	q: string,
	top = 10,
	fuzzy = false,
	index = AZURE_SEARCH_CONFIG.defaultIndex,
) => {
	const oDataFilter = SUGGESTION_CONTENT_TYPES.map(
		(t) => `content_type eq '${t}'`,
	).join(' or ')

	return useQuery({
		queryKey: [index, q],
		queryFn: async () => {
			if (!q) {
				return {
					ok: true,
					status: 200,
					json: {
						'@odata.context': '',
						count: 0,
						facets: null,
						value: [],
					},
				}
			}

			return commonFetch<
				AzureSearchResponse<
					{
						title: string
						url: string
						content_type: string
						content_id: string
					},
					AzureFacets
				>,
				AzureSuggestionParams
			>(
				getAzureEndpoint(index, 'suggest'),
				{
					select: 'title,url,content_type,content_id',
					top,
					fuzzy,
					highlightPostTag: '</b>',
					highlightPreTag: '<b>',
					suggesterName: 'sg',
					search: q,
					filter: oDataFilter,
				},
				{ headers: getAzureHeaders() },
			)
		},
		...DEFAULT_QUERY_CONFIG,
	})
}
